import {
  filtersSelector,
  isUnauthorizedSelector,
} from "@app-redux/selectors";
import { useAppSelector } from "@app-redux/store";
import { AppRouter } from "@components/AppRouter";
import { Loader } from "@components/Loader";
import { AppError } from "@pages/AppError";
import { Invite } from "@pages/Invite";
import { SignedOut } from "@pages/SignedOut";
import { Unauthorized } from "@pages/Unauthorized";
import { ROUTER_PATHS } from "@/constants";
import { useEffect, useMemo } from "react";
import { useAuth } from "./hooks/useAuth";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest, getLoginHint } from "@/config/authConfig";
import { DEV_AUTH_BYPASS } from "@/devAuthBypass";
import "./style/index.css";
import { getCategoryFilters } from "./utils";
import {
  useUserData,
  useCategories,
  useRelationshipLevels,
  useProfileList,
} from "@/hooks/queries";
import { useQueryClient } from "@tanstack/react-query";

export default function App() {
  const queryClient = useQueryClient();
  const filters = useAppSelector(filtersSelector);
  const isUnauthorized = useAppSelector(isUnauthorizedSelector);
  const msalAuthenticated = useIsAuthenticated();
  const isAuthenticated = DEV_AUTH_BYPASS || msalAuthenticated;
  const { instance, inProgress } = useMsal();

  const {
    data: userData,
    error: userDataError,
  } = useUserData();
  const { data: categoryData, map, isCategoryMap } = useCategories();
  useRelationshipLevels();

  useAuth();

  // Trigger login redirect when not authenticated and no interaction in progress.
  useEffect(() => {
    if (DEV_AUTH_BYPASS) return;
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      if (window.location.pathname === `/${ROUTER_PATHS.signedOut}`) return;
      if (window.location.pathname === `/${ROUTER_PATHS.invite}`) return;
      const loginHint = getLoginHint();
      instance.loginRedirect({
        ...loginRequest,
        // With a saved (in-tenant) hint, sign in seamlessly; otherwise force the account picker so a
        // user with multiple accounts is never silently signed into the wrong tenant.
        ...(loginHint ? { loginHint } : { prompt: "select_account" }),
      });
    }
  }, [isAuthenticated, inProgress]);

  // Build profile list filter params from Redux filters + category map
  const profileListFilters = useMemo(() => {
    if (!isCategoryMap) return null;
    const { categories_values, page, ...rest } = filters;
    const categoryFilters = getCategoryFilters(
      map,
      categories_values,
      categoryData
    );
    return { ...rest, ...categoryFilters };
  }, [filters, isCategoryMap, map, categoryData]);

  // Profile list auto-fetches when filters change
  useProfileList(profileListFilters ?? {}, !!profileListFilters);

  if (!isAuthenticated) {
    if (window.location.pathname === `/${ROUTER_PATHS.signedOut}`) {
      return (
        <div className="App">
          <SignedOut />
        </div>
      );
    }
    if (window.location.pathname === `/${ROUTER_PATHS.invite}`) {
      return (
        <div className="App">
          <Invite />
        </div>
      );
    }
    return (
      <div className="App">
        <Loader className="content" show={true} />
      </div>
    );
  }

  if (window.location.pathname === `/${ROUTER_PATHS.invite}`) {
    return (
      <div className="App">
        <Invite />
      </div>
    );
  }

  if (isUnauthorized) {
    return (
      <div className="App">
        <Unauthorized />
      </div>
    );
  }

  if (userDataError && !userData) {
    return (
      <div className="App">
        <AppError
          onRetry={() =>
            queryClient.invalidateQueries({ queryKey: ["userData"] })
          }
        />
      </div>
    );
  }

  return (
    <div className="App">
      <AppRouter />
    </div>
  );
}
